/**
 * ThirdwebWalletButton - Thirdweb-powered wallet connection button
 * 
 * Supports:
 * - In-app wallets (email, Google, Apple, Discord)
 * - Traditional wallets (MetaMask, Coinbase, etc.)
 * - Custom styling to match FreakMe.fun branding
 */
import { ConnectButton, darkTheme } from "thirdweb/react";
import { inAppWallet, createWallet } from "thirdweb/wallets";
import { client, primaryChain, supportedChains } from "../lib/thirdweb";
import { useActiveAccount, useWalletBalance } from "thirdweb/react";
import { useMangoBalance } from "../hooks/useMango";

// Configure wallets - in-app wallet first for easy onboarding
const wallets = [
  inAppWallet({
    auth: {
      options: ["email", "google", "apple", "discord"],
    },
  }),
  createWallet("io.metamask"),
  createWallet("com.coinbase.wallet"),
  createWallet("io.rabby"),
  createWallet("me.rainbow"),
];

// FreakMe.fun branded dark theme
const freakmeTheme = darkTheme({
  colors: {
    primaryButtonBg: "#ff3366",
    primaryButtonText: "#ffffff",
    connectedButtonBg: "#1a1a2e",
    connectedButtonBgHover: "#2a2a4e",
    modalBg: "#0f0f1a",
    borderColor: "#ff336633",
    accentText: "#ff3366",
    accentButtonBg: "#ff3366",
    accentButtonText: "#ffffff",
    secondaryText: "#a0a0b0",
    separatorLine: "#333366",
  },
  fontFamily: "system-ui, -apple-system, sans-serif",
});

interface ThirdwebWalletButtonProps {
  showBalance?: boolean;
  showMango?: boolean;
  label?: string;
}

export default function ThirdwebWalletButton({
  showBalance = false,
  showMango = true,
  label,
}: ThirdwebWalletButtonProps) {
  const account = useActiveAccount();
  const { data: balanceData } = useWalletBalance({
    client,
    chain: primaryChain,
    address: account?.address,
  });
  const { balanceFormatted } = useMangoBalance(account?.address as `0x${string}` | undefined);

  return (
    <ConnectButton
      client={client}
      wallets={wallets}
      chain={primaryChain}
      chains={[...supportedChains]}
      connectButton={{
        label: label || "Connect Wallet",
        style: {
          backgroundColor: "#ff3366",
          color: "#ffffff",
          borderRadius: "8px",
          padding: "10px 20px",
          fontWeight: "600",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "14px",
        },
      }}
      detailsButton={{
        displayBalanceToken: showBalance
          ? {
              [primaryChain.id]: "0x0000000000000000000000000000000000000000", // Native ETH
            }
          : undefined,
        style: {
          backgroundColor: "#1a1a2e",
          color: "#ffffff",
          borderRadius: "8px",
          padding: "10px 16px",
          fontWeight: "500",
          border: "1px solid #ff336633",
          fontSize: "14px",
        },
        render: () => (
          <div className="wallet-btn wallet-connected" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {showMango && balanceFormatted && (
              <span className="mango-balance">🥭 {balanceFormatted}</span>
            )}
            {showBalance && balanceData && (
              <span>{parseFloat(balanceData.displayValue).toFixed(4)} ETH • </span>
            )}
            <span>
              {account?.address
                ? `${account.address.slice(0, 6)}...${account.address.slice(-4)}`
                : "Connected"}
            </span>
          </div>
        ),
      }}
      connectModal={{
        title: "Connect to FreakMe.fun",
        titleIcon: "",
        size: "compact",
        showThirdwebBranding: false,
      }}
      theme={freakmeTheme}
      appMetadata={{
        name: "FreakMe.fun",
        url: "https://freakme.fun",
        description: "Adult content creator platform",
        logoUrl: "https://freakme.fun/logo.svg",
      }}
    />
  );
}

// Simple connect button without custom rendering (for minimal usage)
export function SimpleThirdwebButton({ label = "Connect" }: { label?: string }) {
  return (
    <ConnectButton
      client={client}
      wallets={wallets}
      chain={primaryChain}
      connectButton={{
        label,
        style: {
          backgroundColor: "#ff3366",
          color: "#ffffff",
          borderRadius: "8px",
          padding: "10px 20px",
          fontWeight: "600",
        },
      }}
      theme={freakmeTheme}
    />
  );
}
